import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  Stack,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LaunchIcon from '@mui/icons-material/Launch';
import CodeIcon from '@mui/icons-material/Code';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';

function Projects() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguageContext();
  const t = translations[language];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 8, minHeight: '100vh' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          align="center"
          sx={{ 
            mb: 6,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 'bold'
          }}
        >
          {t.projects.title}
        </Typography>
        <Grid container spacing={4}>
          {t.projects.items.map((project, index) => (
            <Grid item xs={12} key={index}>
              <Card 
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography 
                      variant="h4" 
                      component="h2"
                      color="primary"
                      sx={{ 
                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                        mb: { xs: 2, sm: 0 }
                      }}
                    >
                      {project.title}
                    </Typography>
                    {project.link && (
                      <IconButton 
                        href={project.link} 
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <LaunchIcon color="primary" />
                      </IconButton>
                    )}
                  </Box>

                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={{ xs: 1, sm: 2 }} 
                    sx={{ 
                      mb: { xs: 2, sm: 2 },
                      '& .MuiBox-root': {
                        minWidth: { sm: '200px' }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon 
                        sx={{ 
                          mr: 1, 
                          color: 'primary.main', 
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'rotate(10deg) scale(1.1)',
                          },
                        }} 
                      />
                      <Typography 
                        variant="subtitle1" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        {project.company}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon 
                        sx={{ 
                          mr: 1, 
                          color: 'primary.main', 
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'rotate(10deg) scale(1.1)',
                          },
                        }} 
                      />
                      <Typography 
                        variant="subtitle1" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        {project.location}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarTodayIcon 
                        sx={{ 
                          mr: 1, 
                          color: 'primary.main', 
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'rotate(10deg) scale(1.1)',
                          },
                        }} 
                      />
                      <Typography 
                        variant="subtitle1" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                      >
                        {project.period}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 3,
                      fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {project.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                      }}
                    >
                      <CodeIcon 
                        sx={{ 
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'rotate(10deg) scale(1.1)',
                          },
                        }} 
                      />
                      {t.projects.technologies}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: { xs: 0.5, sm: 1 },
                      '& .MuiChip-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        height: { xs: '24px', sm: '32px' },
                        '& .MuiChip-label': {
                          px: { xs: 1, sm: 2 }
                        }
                      }
                    }}>
                      {project.technologies.map((tech, idx) => (
                        <Chip
                          key={idx}
                          label={tech}
                          variant="outlined"
                          color="primary"
                          sx={{
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'primary.main',
                      fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                    }}
                  >
                    {t.projects.responsibilities}
                  </Typography>
                  <Box 
                    component="ul" 
                    sx={{ 
                      pl: { xs: 1.5, sm: 2 }, 
                      mb: 0,
                      '& li': {
                        pl: 0.5
                      }
                    }}
                  >
                    {project.responsibilities.map((resp, idx) => (
                      <Typography 
                        component="li" 
                        key={idx}
                        sx={{ 
                          mb: 1,
                          color: 'text.secondary',
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          '&:last-child': { mb: 0 }
                        }}
                      >
                        {resp}
                      </Typography>
                    ))}
                  </Box>
                </CardContent>
                {project.link && (
                  <CardActions sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                    <Button 
                      fullWidth={isMobile}
                      variant="contained" 
                      color="primary" 
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<LaunchIcon />}
                      sx={{
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {t.projects.viewProject}
                    </Button>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Personal Projects Section */}
      <Box sx={{ mt: 8 }}>
        <Typography 
          variant="h2" 
          component="h1" 
          align="center"
          sx={{ 
            mb: 6,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            fontWeight: 'bold'
          }}
        >
          {t.personalProjects.title}
        </Typography>
        <Grid container spacing={4}>
          {t.personalProjects.items.map((project, index) => (
            <Grid item xs={12} key={index}>
              <Card 
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography 
                      variant="h4" 
                      component="h2"
                      color="primary"
                      sx={{ 
                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                        mb: { xs: 2, sm: 0 }
                      }}
                    >
                      {project.title}
                    </Typography>
                    {project.link && (
                      <IconButton 
                        href={project.link} 
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <LaunchIcon color="primary" />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CalendarTodayIcon 
                      sx={{ 
                        mr: 1, 
                        color: 'primary.main', 
                        fontSize: { xs: '1.2rem', sm: '1.4rem' },
                        transition: 'transform 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'rotate(10deg) scale(1.1)',
                        },
                      }} 
                    />
                    <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                      {project.period}
                    </Typography>
                  </Box>

                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 3,
                      fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {project.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'primary.main',
                        fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                      }}
                    >
                      <CodeIcon 
                        sx={{ 
                          fontSize: { xs: '1.2rem', sm: '1.4rem' },
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'rotate(10deg) scale(1.1)',
                          },
                        }} 
                      />
                      {t.projects.technologies}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: { xs: 0.5, sm: 1 },
                      '& .MuiChip-root': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        height: { xs: '24px', sm: '32px' },
                        '& .MuiChip-label': {
                          px: { xs: 1, sm: 2 }
                        }
                      }
                    }}>
                      {project.technologies.map((tech, idx) => (
                        <Chip
                          key={idx}
                          label={tech}
                          variant="outlined"
                          color="primary"
                          sx={{
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </CardContent>
                {(project.link || project.linkBackend || project.linkFrontend || project.linkRepo) && (
                  <CardActions sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                    {project.link && (
                      <Button 
                        fullWidth={isMobile}
                        variant="contained" 
                        color="primary" 
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<LaunchIcon />}
                        sx={{
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {t.projects.viewProject}
                      </Button>
                    )}
                    {project.linkBackend && (
                      <Button 
                        fullWidth={isMobile}
                        variant="contained" 
                        color="primary" 
                        href={project.linkBackend}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<LaunchIcon />}
                        sx={{
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {t.projects.viewBackendRepo}
                      </Button>
                    )}
                    {project.linkFrontend && (
                      <Button 
                        fullWidth={isMobile}
                        variant="contained" 
                        color="primary" 
                        href={project.linkFrontend}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<LaunchIcon />}
                        sx={{
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {t.projects.viewFrontendRepo}
                      </Button>
                    )}
                    {project.linkRepo && (
                      <Button 
                        fullWidth={isMobile}
                        variant="contained" 
                        color="primary" 
                        href={project.linkRepo}
                        target="_blank"
                        rel="noopener noreferrer"
                        endIcon={<LaunchIcon />}
                        sx={{
                          transition: 'transform 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {t.projects.viewRepo}
                      </Button>
                    )}
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
}

export default Projects;
